import React, { RefObject } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Popper,
  Paper,
  Grow,
  useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { TopNavMenuItemDto } from "@lib/ShiOneClient";
import { baseUrl, safeTargetProps } from "../utils/topNavHelpers";
import {
  colors,
  slideGradient,
  chevronIdleColor,
  focusVisibleOutline,
  interactiveHighlightSx,
  listItemTextSlotProps,
  subcategoryPaperSx,
  SIDEBAR_WIDTH,
  SUBCATEGORY_Z_INDEX,
} from "../utils/topNavStyles";

interface TopNavCategoryListProps {
  selectedParentItem: TopNavMenuItemDto | undefined;
  anchorEl: HTMLElement | null;
  categorySelected: number | null;
  categoryAnchorEl: HTMLElement | null;
  subcategoryPlacement: "right-start" | "right-end";
  subcategoryPaperRef:RefObject<HTMLDivElement | null>;
  categoryItemRefs:RefObject<Map<number, HTMLElement>>;
  subcategoryItemRefs:RefObject<Map<number, HTMLElement>>;
  hoverDebounceRef:RefObject<ReturnType<
    typeof setTimeout
  > | null>;
  onCategoryHover: (
    e: React.MouseEvent<HTMLElement>,
    menuId: number | undefined,
  ) => void;
  onCategoryKeyDown: (
    e: React.KeyboardEvent<HTMLElement>,
    category: TopNavMenuItemDto,
    categories: TopNavMenuItemDto[],
    index: number,
  ) => void;
  onSubcategoryKeyDown: (
    e: React.KeyboardEvent<HTMLElement>,
    subcategories: TopNavMenuItemDto[],
    index: number,
  ) => void;
  onCategoryDeselect: () => void;
  cancelClose: () => void;
}

export function TopNavCategoryList({
  selectedParentItem,
  anchorEl,
  categorySelected,
  categoryAnchorEl,
  subcategoryPlacement,
  subcategoryPaperRef,
  categoryItemRefs,
  subcategoryItemRefs,
  hoverDebounceRef,
  onCategoryHover,
  onCategoryKeyDown,
  onSubcategoryKeyDown,
  onCategoryDeselect,
  cancelClose,
}: TopNavCategoryListProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;

  return (
    <Box
      className={"shadow"}
      sx={{
        width: SIDEBAR_WIDTH,
        backgroundColor: mode === "dark" ? colors.toolbarDark : colors.sidebarLight,
        pb: 1,
        borderRight: `1px solid ${colors.sidebarBorderLight}`,
        boxShadow: "8px 0 24px -4px rgba(0,0,0,0.15)",
      }}
    >
      <List dense disablePadding role="menu">
        {(selectedParentItem?.children ?? []).map((category, catIndex) => {
          const childLabel = category.displayValue;
          const hasChildren = (category.children?.length ?? 0) > 0;
          const isSelected = category.menuId === categorySelected;

          const childHref =
            category.actionUrl && category.actionUrl !== "#"
              ? baseUrl + category.actionUrl
              : undefined;

          return (
            <ListItemButton
              key={category.menuId}
              component="a"
              href={childHref ?? "#"}
              ref={(el: HTMLElement | null) => {
                if (el && category.menuId != null) {
                  categoryItemRefs.current.set(category.menuId, el);
                }
              }}
              {...safeTargetProps(category.actionTarget)}
              role="menuitem"
              aria-haspopup={hasChildren ? "menu" : undefined}
              aria-expanded={hasChildren ? isSelected : undefined}
              tabIndex={-1}
              selected={isSelected}
              onMouseEnter={(e) => onCategoryHover(e, category.menuId)}
              onMouseLeave={() => {
                if (hoverDebounceRef.current)
                  clearTimeout(hoverDebounceRef.current);
              }}
              onKeyDown={(e) =>
                onCategoryKeyDown(
                  e,
                  category,
                  selectedParentItem?.children ?? [],
                  catIndex,
                )
              }
              onFocus={() => {
                cancelClose();
              }}
              sx={{
                px: 2,
                display: "flex",
                alignItems: "center",
                background: slideGradient(mode),
                backgroundSize: "200% 100%",
                backgroundPosition: isSelected ? "0 0" : "100% 0",
                transition: "background-position 0.3s ease, color 0.3s ease",
                color: isSelected ? colors.white : "inherit",
                "&.Mui-selected": {
                  backgroundColor: "transparent !important",
                },
                "&:hover": {
                  backgroundPosition: "0 0",
                  color: colors.white,
                },
                "&:focus-visible": {
                  backgroundPosition: "0 0",
                  color: colors.white,
                  ...focusVisibleOutline(theme),
                },
                "&:hover .MuiSvgIcon-root": { color: colors.white },
                "&:focus-visible .MuiSvgIcon-root": { color: colors.white },
              }}
            >
              <ListItemText
                primary={childLabel}
                slotProps={listItemTextSlotProps}
              />
              {hasChildren && (
                <ChevronRightIcon
                  fontSize="small"
                  sx={{
                    color: isSelected
                      ? colors.white
                      : chevronIdleColor(mode),
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
        {anchorEl && (selectedParentItem?.children?.length ?? 0) === 0 && (
          <Box
            sx={{
              px: 2,
              py: 2,
              color: colors.emptyStateText,
              fontSize: 14,
            }}
          >
            No categories
          </Box>
        )}
      </List>
      <Popper
        open={categorySelected !== null}
        anchorEl={categoryAnchorEl}
        placement={subcategoryPlacement}
        transition
        onMouseLeave={() => {
          onCategoryDeselect();
        }}
        sx={{ zIndex: SUBCATEGORY_Z_INDEX }}
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 2],
              },
            },
          ],
        }}
      >
        {({ TransitionProps: FlyoutTransitionProps }) => {
          const subcategories =
            selectedParentItem?.children?.find(
              (item) => item.menuId === categorySelected,
            )?.children ?? [];

          return (
            <Grow {...FlyoutTransitionProps}>
              <Paper
                elevation={5}
                ref={subcategoryPaperRef}
                sx={subcategoryPaperSx}
              >
                <List dense disablePadding role="menu">
                  {subcategories.map((gc, gcIndex) => {
                    const href =
                      gc.actionUrl && gc.actionUrl !== "#"
                        ? baseUrl + gc.actionUrl
                        : "#";

                    if (!gc) return false;

                    return (
                      <ListItemButton
                        key={gc.menuId}
                        component="a"
                        href={href}
                        ref={(el: HTMLElement | null) => {
                          if (el && gc.menuId != null) {
                            subcategoryItemRefs.current.set(gc.menuId, el);
                          }
                        }}
                        {...safeTargetProps(gc.actionTarget)}
                        role="menuitem"
                        tabIndex={-1}
                        onKeyDown={(e) =>
                          onSubcategoryKeyDown(e, subcategories, gcIndex)
                        }
                        onFocus={() => cancelClose()}
                        sx={interactiveHighlightSx(theme)}
                      >
                        <ListItemText
                          primary={gc.displayValue}
                          slotProps={listItemTextSlotProps}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Paper>
            </Grow>
          );
        }}
      </Popper>
    </Box>
  );
}
